import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Car, ArrowLeft, Save, Loader2, Edit2, Trash2, AlertTriangle,
  FileText, Gauge, FolderOpen, Calendar, User, Fuel, Settings,
  ChevronRight, Upload, Download, Eye, X, RotateCcw
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { DatePickerFr } from "../../components/ui/date-picker-fr";
import GeneralInfoTab from "../../components/fleet/tabs/GeneralInfoTab";
import ContractTab from "../../components/fleet/tabs/ContractTab";
import MileageTab from "../../components/fleet/tabs/MileageTab";
import DocumentsTab from "../../components/fleet/tabs/DocumentsTab";
import CostsTab from "../../components/fleet/tabs/CostsTab";
import MaintenanceTab from "../../components/fleet/tabs/MaintenanceTab";
import VehicleTcoCard from "../../components/fleet/VehicleTcoCard";
import LinkedEngagementsCard from "../../components/fleet/LinkedEngagementsCard";
import InsuranceTab from "../../components/fleet/tabs/InsuranceTab";
import ClaimsTab from "../../components/fleet/tabs/ClaimsTab";
import {
  VEHICLE_STATUS_LABELS,
  ACQUISITION_TYPE_LABELS,
  MILEAGE_STATUS_COLORS,
  formatMileage,
  formatDate,
  getDaysUntilLabel,
  getStatusColor,
} from "../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("general");
  const [deleteDialog, setDeleteDialog] = useState(false);
  
  const token = localStorage.getItem("token");

  const fetchVehicle = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fleet/vehicles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicle(response.data);
    } catch (err) {
      console.error("Error fetching vehicle:", err);
      if (err.response?.status === 404) {
        toast.error("Véhicule non trouvé");
        navigate("/fleet/vehicles");
      } else {
        toast.error("Erreur lors du chargement");
      }
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    fetchVehicle();
  }, [fetchVehicle]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/fleet/vehicles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Véhicule déplacé vers la corbeille");
      navigate("/fleet/vehicles");
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleUpdate = () => {
    fetchVehicle();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Véhicule non trouvé</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/fleet/vehicles")}>
            Retour à la liste
          </Button>
        </div>
      </div>
    );
  }

  const mileageStats = vehicle.mileage_stats;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-4 sm:px-6 py-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
            <Link to="/fleet/vehicles" className="hover:text-slate-700">Parc Auto</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900">{vehicle.brand} {vehicle.model}</span>
          </div>
          
          {/* Main header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/fleet/vehicles")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              
              <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Car className="w-7 h-7 text-indigo-600" />
              </div>
              
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-900">
                    {vehicle.brand} {vehicle.model}
                  </h1>
                  <Badge className={getStatusColor(vehicle.status)}>
                    {VEHICLE_STATUS_LABELS[vehicle.status]}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                  <span className="font-mono">{vehicle.registration}</span>
                  <span>•</span>
                  <span>{vehicle.internal_code}</span>
                  {vehicle.driver_name && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {vehicle.driver_name}
                      </span>
                    </>
                  )}
                </div>
                {vehicle.linked_engagement_ids && vehicle.linked_engagement_ids.length > 0 && (
                  <div className="mt-1.5 flex items-center gap-1 text-xs">
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">
                      <Link className="hidden" to="#" />
                      🔗 {vehicle.linked_engagement_ids.length} contrat{vehicle.linked_engagement_ids.length > 1 ? "s" : ""} lié{vehicle.linked_engagement_ids.length > 1 ? "s" : ""}
                    </Badge>
                    <button
                      type="button"
                      onClick={() => setActiveTab("contract")}
                      className="text-emerald-700 hover:underline"
                      data-testid="view-linked-engagements-btn"
                    >
                      voir détail →
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-2 ml-14 sm:ml-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteDialog(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Supprimer</span>
              </Button>
            </div>
          </div>
        </div>
        
        {/* Quick Stats Bar */}
        {vehicle.active_contract && (
          <div className="px-4 sm:px-6 py-3 bg-slate-50 border-t flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">Restitution:</span>
              <span className={`font-medium ${
                vehicle.days_until_return <= 30 ? "text-red-600" :
                vehicle.days_until_return <= 90 ? "text-orange-600" : "text-slate-900"
              }`}>
                {formatDate(vehicle.contract_end_date)} ({getDaysUntilLabel(vehicle.days_until_return)})
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <Gauge className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">Kilométrage:</span>
              <span className="font-medium">{formatMileage(vehicle.current_mileage)}</span>
              {mileageStats && mileageStats.status !== "green" && (
                <Badge className={MILEAGE_STATUS_COLORS[mileageStats.status]}>
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {mileageStats.status === "red" ? "Dépassement" : "Attention"}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-slate-600">Type:</span>
              <Badge variant="outline">
                {ACQUISITION_TYPE_LABELS[vehicle.acquisition_type]}
              </Badge>
            </div>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start bg-white border rounded-lg p-1 mb-6 overflow-x-auto flex-nowrap">
            <TabsTrigger value="general" className="flex items-center gap-2 whitespace-nowrap" data-testid="tab-general">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Infos générales</span>
              <span className="sm:hidden">Infos</span>
            </TabsTrigger>
            <TabsTrigger value="contract" className="flex items-center gap-2 whitespace-nowrap" data-testid="tab-contract">
              <FileText className="w-4 h-4" />
              <span>Contrat</span>
            </TabsTrigger>
            <TabsTrigger value="mileage" className="flex items-center gap-2 whitespace-nowrap" data-testid="tab-mileage">
              <Gauge className="w-4 h-4" />
              <span>Km</span>
              {mileageStats && mileageStats.status !== "green" && (
                <span className={`w-2 h-2 rounded-full ${
                  mileageStats.status === "red" ? "bg-red-500" : "bg-orange-500"
                }`} />
              )}
            </TabsTrigger>
            <TabsTrigger value="costs" className="flex items-center gap-2 whitespace-nowrap" data-testid="tab-costs">
              <span className="text-lg">€</span>
              <span>Coûts</span>
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="flex items-center gap-2 whitespace-nowrap" data-testid="tab-maintenance">
              <span className="text-lg">🔧</span>
              <span>Entretien</span>
            </TabsTrigger>
            <TabsTrigger value="insurance" className="flex items-center gap-2 whitespace-nowrap" data-testid="tab-insurance">
              <span className="text-lg">🛡️</span>
              <span>Assurance</span>
            </TabsTrigger>
            <TabsTrigger value="claims" className="flex items-center gap-2 whitespace-nowrap" data-testid="tab-claims">
              <AlertTriangle className="w-4 h-4" />
              <span>Sinistres</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2 whitespace-nowrap" data-testid="tab-documents">
              <FolderOpen className="w-4 h-4" />
              <span>Docs</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="general">
            <GeneralInfoTab vehicle={vehicle} onUpdate={handleUpdate} />
          </TabsContent>
          
          <TabsContent value="contract">
            <div className="space-y-4">
              <LinkedEngagementsCard vehicleId={vehicle.id} token={token} />
              <ContractTab vehicle={vehicle} onUpdate={handleUpdate} />
            </div>
          </TabsContent>
          
          <TabsContent value="mileage">
            <MileageTab vehicle={vehicle} onUpdate={handleUpdate} />
          </TabsContent>
          
          <TabsContent value="costs">
            <div className="space-y-4">
              <VehicleTcoCard vehicleId={vehicle.id} token={token} />
              <CostsTab vehicleId={vehicle.id} />
            </div>
          </TabsContent>
          
          <TabsContent value="maintenance">
            <MaintenanceTab vehicleId={vehicle.id} currentMileage={vehicle.current_mileage} />
          </TabsContent>
          
          <TabsContent value="insurance">
            <InsuranceTab vehicleId={vehicle.id} />
          </TabsContent>
          
          <TabsContent value="claims">
            <ClaimsTab vehicleId={vehicle.id} />
          </TabsContent>
          
          <TabsContent value="documents">
            <DocumentsTab vehicle={vehicle} onUpdate={handleUpdate} />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce véhicule ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le véhicule <strong>{vehicle.brand} {vehicle.model}</strong> ({vehicle.registration}) 
              sera déplacé vers la corbeille. Vous pourrez le restaurer pendant 30 jours.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
